import { Fragment, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Search,
  Filter,
  BadgeIndianRupee,
  ArrowLeft,
  Trash2,
  FolderOpen,
  PackageSearch,
  ImageOff,
  AlertTriangle,
  Download,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { toast } from "sonner";
import { useInventoryScans, isScanExpired, formatRelativeTime, formatScanExpiration } from "../../../lib/extension-bridge";
import { api, getErrorMessage } from "../../../lib/api";

const STATUS_OPTIONS = ["Active", "OOS", "Low Stock", "Paused"];
const STATUS_DOT = {
  Active: "bg-emerald-500",
  OOS: "bg-rose-500",
  "Low Stock": "bg-amber-500",
  Paused: "bg-slate-400",
};
const STATUS_TEXT_STYLES = {
  Active: "text-emerald-600",
  OOS: "text-rose-600",
  "Low Stock": "text-amber-600",
  Paused: "text-slate-500",
};

const statusCounts = (scan) => {
  const counts = { Active: 0, OOS: 0, "Low Stock": 0, Paused: 0 };
  scan.products.forEach((p) => {
    counts[p.status] = (counts[p.status] || 0) + 1;
  });
  return counts;
};

const ScanCard = ({ scan, onOpen, onDelete }) => {
  const expired = isScanExpired(scan);
  const counts = statusCounts(scan);

  return (
    <div
      onClick={onOpen}
      data-testid={`scan-card-${scan.id}`}
      className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-xl hover:shadow-purple-600/5 hover:-translate-y-0.5 hover:border-purple-200 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-slate-500 font-medium">{formatRelativeTime(scan.scannedAt)}</div>
        <Badge className={`rounded-full text-[10px] font-bold hover:opacity-90 ${expired ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
          {formatScanExpiration(scan)}
        </Badge>
      </div>

      <div className="mt-3 font-heading text-lg font-extrabold text-slate-900">
        Total Products : {scan.products.length.toLocaleString("en-IN")}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
        {STATUS_OPTIONS.map((s) => (
          <span key={s} className={STATUS_TEXT_STYLES[s]}>
            {s} : {counts[s] ?? 0}
          </span>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-1.5 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant="outline"
          onClick={onOpen}
          data-testid={`scan-open-${scan.id}`}
          className="flex-1 rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold h-8 text-xs"
        >
          <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Open
        </Button>
        <button
          onClick={onDelete}
          data-testid={`scan-delete-${scan.id}`}
          aria-label="Delete scan"
          className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const GridView = ({ scans, onOpen, onDelete }) => (
  <div className="space-y-6" data-testid="price-manager-grid">
    <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50/60 to-white p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-lg shadow-purple-600/25 shrink-0">
          <BadgeIndianRupee className="w-5 h-5" />
        </div>
        <div>
          <div className="font-heading text-lg font-extrabold text-slate-900">Price Manager</div>
          <div className="text-sm text-slate-600 mt-1">
            Scans expire after 48 hours · {scans.length} scan{scans.length === 1 ? "" : "s"} stored
          </div>
        </div>
      </div>
      <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-4 py-2">
        Use the <strong className="text-purple-700">Scan</strong> button in the EasyLister extension on the Meesho Inventory page to add a scan here.
      </div>
    </div>

    {scans.length === 0 ? (
      <div className="rounded-3xl border border-dashed border-purple-200 bg-white p-12 text-center" data-testid="price-manager-empty">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-4">
          <PackageSearch className="w-6 h-6" />
        </div>
        <div className="font-heading text-base font-bold text-slate-900">No scans yet</div>
        <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
          Open the Meesho Inventory page, click the EasyLister extension icon, then hit <strong>Scan</strong>. Results show up here automatically.
        </p>
      </div>
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {scans.map((scan) => (
          <ScanCard key={scan.id} scan={scan} onOpen={() => onOpen(scan.id)} onDelete={() => onDelete(scan.id)} />
        ))}
      </div>
    )}
  </div>
);

// Plain input used on a catalog group row — typing a value and tabbing/clicking away
// (or pressing Enter) applies it to every variant in that catalog at once, then clears.
const GroupBulkInput = ({ value, onChange, onCommit, disabled, testId }) => (
  <Input
    type="number"
    placeholder="₹ All"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter") e.currentTarget.blur();
    }}
    onBlur={(e) => {
      if (e.target.value !== "") onCommit(e.target.value);
    }}
    disabled={disabled}
    data-testid={testId}
    className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
  />
);

const TableView = ({ scan, onBack, onSaveProducts, onSaveSettings }) => {
  const expired = isScanExpired(scan);

  const [products, setProducts] = useState(scan.products);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [expandedCatalogs, setExpandedCatalogs] = useState(() => new Set());
  const [groupBulkValues, setGroupBulkValues] = useState({});
  const [exporting, setExporting] = useState(false);

  const saveTimer = useRef(null);
  const persist = (next) => {
    setProducts(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSaveProducts(scan.id, next), 400);
  };

  const updateRow = (id, patch) => {
    persist(products.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const handleMSPChange = (id, value) => {
    const newMSP = value === "" ? null : Number(value);
    const patch = { newMSP };
    if (scan.autoWDRP) patch.newWDRP = newMSP === null ? null : newMSP - scan.autoWDRPDeduction;
    updateRow(id, patch);
  };

  const toggleAutoWDRP = (checked) => {
    onSaveSettings(scan.id, { autoWDRP: checked });
    if (checked) {
      persist(products.map((p) => (p.newMSP !== null ? { ...p, newWDRP: p.newMSP - scan.autoWDRPDeduction } : p)));
    }
  };

  const changeDeduction = (value) => {
    const deduction = value === "" ? 0 : Number(value);
    onSaveSettings(scan.id, { autoWDRPDeduction: deduction });
    if (scan.autoWDRP) {
      persist(products.map((p) => (p.newMSP !== null ? { ...p, newWDRP: p.newMSP - deduction } : p)));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch = !q || p.name.toLowerCase().includes(q) || p.styleId.toLowerCase().includes(q);
      const matchesStatus = statusFilter.size === 0 || statusFilter.has(p.status);
      return matchesSearch && matchesStatus;
    });
  }, [products, search, statusFilter]);

  // Group the filtered (search/status-matched) rows by catalog, preserving first-seen order.
  const groups = useMemo(() => {
    const byId = new Map();
    for (const p of filtered) {
      if (!byId.has(p.catalogId)) {
        byId.set(p.catalogId, { catalogId: p.catalogId, name: p.name, image: p.image, category: p.category, variants: [] });
      }
      byId.get(p.catalogId).variants.push(p);
    }
    return [...byId.values()];
  }, [filtered]);

  const toggleCatalogExpand = (catalogId) => {
    setExpandedCatalogs((prev) => {
      const next = new Set(prev);
      if (next.has(catalogId)) next.delete(catalogId);
      else next.add(catalogId);
      return next;
    });
  };

  const groupSelectionState = (group) => {
    const all = group.variants.every((v) => selected.has(v.id));
    if (all) return true;
    return group.variants.some((v) => selected.has(v.id)) ? "indeterminate" : false;
  };

  const toggleGroupSelect = (group) => {
    const allSelected = group.variants.every((v) => selected.has(v.id));
    setSelected((prev) => {
      const next = new Set(prev);
      group.variants.forEach((v) => (allSelected ? next.delete(v.id) : next.add(v.id)));
      return next;
    });
  };

  const groupBulkKey = (catalogId, field) => `${catalogId}|${field}`;
  const getGroupBulkValue = (catalogId, field) => groupBulkValues[groupBulkKey(catalogId, field)] ?? "";
  const setGroupBulkValue = (catalogId, field, value) =>
    setGroupBulkValues((prev) => ({ ...prev, [groupBulkKey(catalogId, field)]: value }));

  const applyGroupBulk = (group, field, rawValue) => {
    if (rawValue === "") return;
    const value = Number(rawValue);
    const ids = new Set(group.variants.map((v) => v.id));
    persist(
      products.map((p) => {
        if (!ids.has(p.id)) return p;
        if (field === "newMSP") {
          const next = { newMSP: value };
          if (scan.autoWDRP) next.newWDRP = value - scan.autoWDRPDeduction;
          return { ...p, ...next };
        }
        return { ...p, [field]: value };
      }),
    );
    setGroupBulkValue(group.catalogId, field, "");
    toast.success(`Applied to ${ids.size} variant${ids.size === 1 ? "" : "s"} in "${group.name}"`);
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const someFilteredSelected = filtered.some((p) => selected.has(p.id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((p) => next.delete(p.id));
      else filtered.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportXlsx = async () => {
    const hasEdits = (p) => p.newMSP !== null || p.newWDRP !== null || p.newMRP !== null;
    // Nothing selected -> export every changed row. Selection made -> export exactly that selection.
    const rows = selected.size > 0 ? products.filter((p) => selected.has(p.id)) : products.filter(hasEdits);

    if (rows.length === 0) {
      toast.error(selected.size > 0 ? "Selected rows have no data to export" : "No updated rows to export");
      return;
    }

    setExporting(true);
    try {
      await api.post("/protected/subscription/track-usage", { feature: "xlsxExport" });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Subscription required", { description: "Please subscribe to export listings." });
      } else if (status === 429) {
        toast.error("Daily export limit reached", { description: "Resets at midnight IST." });
      } else {
        toast.error("Export failed", { description: getErrorMessage(err) });
      }
      setExporting(false);
      return;
    }

    const headers = [
      "Catalog ID",
      "Product ID",
      "Variation Name",
      "Variation ID",
      "New Meesho Selling Price (MSP)",
      "Wrong/Defective Return Price (WDRP) (if catalog is part of WDRP)",
      "New Maximum Retail Price (MRP) (Optional)",
    ];
    const data = [
      headers,
      ...rows.map((p) => [p.catalogId, p.productId, p.variationName, p.variationId, p.newMSP ?? "", p.newWDRP ?? "", p.newMRP ?? ""]),
    ];

    const sheet = XLSX.utils.aoa_to_sheet(data);
    sheet["!cols"] = [{ wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 22 }, { wch: 40 }, { wch: 24 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Price Update");
    XLSX.writeFile(workbook, `price-update-${scan.id}.xlsx`);

    setExporting(false);
    toast.success("File exported successfully", { description: `${rows.length} row${rows.length === 1 ? "" : "s"} exported.` });
  };

  return (
    <div className="space-y-4" data-testid="price-manager-table-view">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          data-testid="table-back-button"
          className="p-2 rounded-full text-slate-500 hover:text-purple-700 hover:bg-purple-50"
          aria-label="Back to scans"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base font-extrabold text-slate-900">Scan · {formatRelativeTime(scan.scannedAt)}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {products.length} products{selected.size > 0 ? ` · ${selected.size} selected` : ""}
          </div>
        </div>
        <Badge className={`rounded-full text-[10px] font-bold hover:opacity-90 ${expired ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
          {formatScanExpiration(scan)}
        </Badge>
        <Button
          size="sm"
          onClick={exportXlsx}
          disabled={exporting}
          data-testid="export-xlsx-button"
          className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold h-9 text-xs"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          {exporting ? "Exporting…" : "Export XLSX"}
        </Button>
      </div>

      {expired && (
        <div
          data-testid="scan-expired-banner"
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Scan expired. Please scan inventory again.
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
        {/* Search, status filter, Auto WDRP, bulk MSP/WDRP/MRP, selection — all in one toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 bg-purple-50/30" data-testid="table-toolbar">
          <div className="flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 h-9 w-56 max-w-full">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              data-testid="table-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or Style ID"
              className="flex-1 text-xs bg-transparent outline-none placeholder:text-slate-400 min-w-0"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="table-status-filter-trigger"
                className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold h-9 text-xs"
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                Status{statusFilter.size > 0 ? ` (${statusFilter.size})` : ""}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {STATUS_OPTIONS.map((s) => (
                <DropdownMenuCheckboxItem
                  key={s}
                  checked={statusFilter.has(s)}
                  onCheckedChange={(checked) => {
                    setStatusFilter((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(s);
                      else next.delete(s);
                      return next;
                    });
                  }}
                  data-testid={`table-status-filter-${s.toLowerCase()}`}
                >
                  {s}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="h-5 w-px bg-slate-200" />

          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={scan.autoWDRP} onCheckedChange={(c) => toggleAutoWDRP(c === true)} disabled={expired} data-testid="auto-wdrp-checkbox" />
            Auto WDRP
          </label>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            Deduction :
            <Input
              type="number"
              value={scan.autoWDRPDeduction}
              onChange={(e) => changeDeduction(e.target.value)}
              disabled={!scan.autoWDRP || expired}
              data-testid="auto-wdrp-deduction-input"
              className="w-16 h-7 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
            />
          </div>

          <div className="h-5 w-px bg-slate-200" />

          <Button size="sm" variant="outline" onClick={selectAllFiltered} data-testid="bulk-select-all" className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold h-8 text-xs">
            Select All
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear-selection" className="rounded-full text-slate-500 hover:bg-slate-50 font-semibold h-8 text-xs">
            Clear Selection
          </Button>
        </div>

        {/* Table — wrapperClassName owns the actual scroll container so the
            sticky header sticks to it, not the Table component's own (no-max-height) wrapper */}
        <Table wrapperClassName="max-h-[60vh] overflow-auto">
          <TableHeader className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_theme(colors.slate.200)]">
            <TableRow>
                <TableHead className="w-9" />
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Image</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Product</TableHead>
                <TableHead className="w-24 text-[11px] font-bold uppercase tracking-wider text-slate-500">Style ID</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Current MSP</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Current WDRP</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">New MSP ✱</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">New WDRP</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">New MRP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => {
                const isSingle = group.variants.length === 1;
                const single = isSingle ? group.variants[0] : null;
                const isExpanded = expandedCatalogs.has(group.catalogId);
                const allSameStatus = group.variants.every((v) => v.status === group.variants[0].status);
                const statusSample = isSingle ? single : group.variants[0];

                return (
                  <Fragment key={group.catalogId}>
                    <TableRow data-testid={`catalog-row-${group.catalogId}`} className={isSingle ? "" : "bg-slate-50/60"}>
                      <TableCell>
                        <Checkbox
                          checked={groupSelectionState(group)}
                          onCheckedChange={() => toggleGroupSelect(group)}
                          data-testid={`catalog-checkbox-${group.catalogId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                          {group.image ? (
                            <img src={group.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <ImageOff className="w-3.5 h-3.5 text-slate-300" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <div className="flex items-center gap-1.5">
                          {!isSingle && (
                            <button
                              onClick={() => toggleCatalogExpand(group.catalogId)}
                              data-testid={`catalog-expand-${group.catalogId}`}
                              className="p-0.5 text-slate-400 hover:text-purple-600 shrink-0"
                              aria-label={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900 text-[13px]" title={group.name}>
                              {group.name || "—"}
                            </div>
                            <div className="text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="text-slate-400">{group.variants.length} variant{group.variants.length === 1 ? "" : "s"}</span>
                              {group.category && (
                                <>
                                  <span className="text-slate-300">·</span>
                                  <span className="text-purple-600 font-medium">{group.category}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[96px]">
                        {isSingle && single.styleId ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-600 truncate max-w-[88px]">
                            {single.styleId}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isSingle || allSameStatus ? (
                          <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${STATUS_TEXT_STYLES[statusSample.status] || "text-slate-500"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[statusSample.status] || "bg-slate-400"}`} />
                            {statusSample.status}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 font-semibold">Mixed</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums font-semibold text-slate-800">{isSingle ? `₹${single.currentMSP}` : <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="tabular-nums font-semibold text-slate-800">{isSingle ? `₹${single.currentWDRP}` : <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell>
                        {isSingle ? (
                          <Input
                            type="number"
                            value={single.newMSP ?? ""}
                            placeholder={String(single.currentMSP)}
                            onChange={(e) => handleMSPChange(single.id, e.target.value)}
                            disabled={expired}
                            data-testid={`new-msp-${single.id}`}
                            className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                          />
                        ) : (
                          <GroupBulkInput
                            value={getGroupBulkValue(group.catalogId, "newMSP")}
                            onChange={(v) => setGroupBulkValue(group.catalogId, "newMSP", v)}
                            onCommit={(v) => applyGroupBulk(group, "newMSP", v)}
                            disabled={expired}
                            testId={`group-msp-${group.catalogId}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isSingle ? (
                          <Input
                            type="number"
                            value={single.newWDRP ?? ""}
                            placeholder={String(single.currentWDRP)}
                            onChange={(e) => updateRow(single.id, { newWDRP: e.target.value === "" ? null : Number(e.target.value) })}
                            disabled={expired}
                            data-testid={`new-wdrp-${single.id}`}
                            className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                          />
                        ) : (
                          <GroupBulkInput
                            value={getGroupBulkValue(group.catalogId, "newWDRP")}
                            onChange={(v) => setGroupBulkValue(group.catalogId, "newWDRP", v)}
                            onCommit={(v) => applyGroupBulk(group, "newWDRP", v)}
                            disabled={expired}
                            testId={`group-wdrp-${group.catalogId}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isSingle ? (
                          <Input
                            type="number"
                            value={single.newMRP ?? ""}
                            onChange={(e) => updateRow(single.id, { newMRP: e.target.value === "" ? null : Number(e.target.value) })}
                            disabled={expired}
                            data-testid={`new-mrp-${single.id}`}
                            className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                          />
                        ) : (
                          <GroupBulkInput
                            value={getGroupBulkValue(group.catalogId, "newMRP")}
                            onChange={(v) => setGroupBulkValue(group.catalogId, "newMRP", v)}
                            onCommit={(v) => applyGroupBulk(group, "newMRP", v)}
                            disabled={expired}
                            testId={`group-mrp-${group.catalogId}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>

                    {!isSingle &&
                      isExpanded &&
                      group.variants.map((v) => (
                        <TableRow key={v.id} data-testid={`product-row-${v.id}`}>
                          <TableCell>
                            <Checkbox checked={selected.has(v.id)} onCheckedChange={() => toggleRow(v.id)} data-testid={`row-checkbox-${v.id}`} />
                          </TableCell>
                          <TableCell />
                          <TableCell className="pl-7 text-xs text-slate-600">
                            {v.variationName || "—"}
                            {v.variationId ? ` (#${v.variationId})` : ""}
                          </TableCell>
                          <TableCell className="max-w-[96px]">
                            {v.styleId ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-600 truncate max-w-[88px]">
                                {v.styleId}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${STATUS_TEXT_STYLES[v.status] || "text-slate-500"}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[v.status] || "bg-slate-400"}`} />
                              {v.status}
                            </span>
                          </TableCell>
                          <TableCell className="tabular-nums font-semibold text-slate-800">₹{v.currentMSP}</TableCell>
                          <TableCell className="tabular-nums font-semibold text-slate-800">₹{v.currentWDRP}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={v.newMSP ?? ""}
                              placeholder={String(v.currentMSP)}
                              onChange={(e) => handleMSPChange(v.id, e.target.value)}
                              disabled={expired}
                              data-testid={`new-msp-${v.id}`}
                              className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={v.newWDRP ?? ""}
                              placeholder={String(v.currentWDRP)}
                              onChange={(e) => updateRow(v.id, { newWDRP: e.target.value === "" ? null : Number(e.target.value) })}
                              disabled={expired}
                              data-testid={`new-wdrp-${v.id}`}
                              className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={v.newMRP ?? ""}
                              onChange={(e) => updateRow(v.id, { newMRP: e.target.value === "" ? null : Number(e.target.value) })}
                              disabled={expired}
                              data-testid={`new-mrp-${v.id}`}
                              className="w-24 h-8 rounded-lg border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2 tabular-nums"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </Fragment>
                );
              })}

              {groups.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-500 text-sm">
                    No products match your search / filter.
                  </td>
                </tr>
              )}
            </TableBody>
        </Table>
      </div>
    </div>
  );
};

export const PriceManagerPage = () => {
  const { scans, deleteScan, saveProducts, saveSettings } = useInventoryScans();
  const [openScanId, setOpenScanId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const openScan = scans.find((s) => s.id === openScanId) || null;

  const confirmDelete = () => {
    deleteScan(deletingId);
    if (openScanId === deletingId) setOpenScanId(null);
    toast.success("Scan deleted");
    setDeletingId(null);
  };

  return (
    <div data-testid="panel-price-manager">
      {openScan ? (
        <TableView
          key={openScan.id}
          scan={openScan}
          onBack={() => setOpenScanId(null)}
          onSaveProducts={saveProducts}
          onSaveSettings={saveSettings}
        />
      ) : (
        <GridView scans={scans} onOpen={setOpenScanId} onDelete={setDeletingId} />
      )}

      <AlertDialog open={Boolean(deletingId)} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent data-testid="scan-delete-confirm" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this scan?</AlertDialogTitle>
            <AlertDialogDescription>This removes all scanned products in this batch. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="scan-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="scan-delete-confirm-button" className="rounded-full bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
